import {AttributeBulkFieldPayload, AttributeBulkImportPayload} from '../admin/attribute-bulk-import.template';
import {AttributeDefinitionView, ListingAttributeValueView} from './attribute-schema.service';

export const DEFAULT_FORM_SECTION_TITLE = 'General';
export const DATATYPE_PALETTE_ID = 'datatype-palette';

export type MultiEnumUi = 'select' | 'checkbox';

export interface DataTypeTemplate {
  dataType: string;
  title: string;
  description: string;
  icon: string;
  multiEnumUi?: MultiEnumUi;
}

export interface FormFieldDraft {
  draftId: string;
  dataType: string;
  code: string;
  label: string;
  unit: string;
  required: boolean;
  filterable: boolean;
  searchable: boolean;
  options: Array<{key: string; label: string; sortOrder: number}>;
  rangeMin: number;
  rangeMax: number;
  rangeStep: number;
  textMinLength: number | null;
  textMaxLength: number | null;
  textPlaceholder: string;
  numberMin: number | null;
  numberMax: number | null;
  collapsed: boolean;
  multiEnumUi?: MultiEnumUi;
  optionsCollapsed?: boolean;
  optionsJsonMode?: boolean;
  optionsJsonDraft?: string;
  showWhenEnabled?: boolean;
  showWhenAttribute?: string;
  showWhenIn?: string;
  showWhenNotIn?: string;
  requiredWhenEnabled?: boolean;
  requiredWhenAttribute?: string;
  requiredWhenIn?: string;
  requiredWhenNotIn?: string;
}

export interface FormBuilderSection {
  id: string;
  title: string;
  fields: FormFieldDraft[];
}

export interface AttributeSectionGroup {
  title: string;
  fields: AttributeDefinitionView[];
}

/** Shared shape for showWhen / requiredWhen rules on validationJson. */
export interface AttributeConditionRule {
  attribute: string;
  in?: string[];
  notIn?: string[];
}

/** @deprecated Use AttributeConditionRule */
export type ShowWhenRule = AttributeConditionRule;

export interface FormBuilderValidationIssue {
  sectionIndex: number;
  fieldIndex: number;
  message: string;
}

export interface FormFieldDraftExport {
  dataType: string;
  code: string;
  label: string;
  unit: string;
  required: boolean;
  filterable: boolean;
  searchable: boolean;
  options: Array<{key: string; label: string; sortOrder: number}>;
  rangeMin: number;
  rangeMax: number;
  rangeStep: number;
  textMinLength: number | null;
  textMaxLength: number | null;
  textPlaceholder: string;
  numberMin: number | null;
  numberMax: number | null;
  collapsed?: boolean;
  multiEnumUi?: MultiEnumUi;
  showWhenEnabled?: boolean;
  showWhenAttribute?: string;
  showWhenIn?: string;
  showWhenNotIn?: string;
  requiredWhenEnabled?: boolean;
  requiredWhenAttribute?: string;
  requiredWhenIn?: string;
  requiredWhenNotIn?: string;
}

export interface FormBuilderDraftPayload {
  version: 1;
  categoryPath: string;
  importMode?: 'upsert' | 'create_only';
  sections: Array<{
    title: string;
    fields: FormFieldDraftExport[];
  }>;
}

export interface FormBuilderDraftImportResult {
  categoryPath: string;
  importMode: 'upsert' | 'create_only';
  sections: FormBuilderSection[];
}

export const DATA_TYPE_TEMPLATES: DataTypeTemplate[] = [
  {dataType: 'STRING', title: 'Text', description: 'Single-line text', icon: 'text_fields'},
  {dataType: 'INTEGER', title: 'Whole number', description: 'Integer value', icon: 'pin'},
  {dataType: 'DECIMAL', title: 'Decimal number', description: 'Number with decimals', icon: 'numbers'},
  {dataType: 'BOOLEAN', title: 'Yes / No', description: 'Checkbox', icon: 'check_box'},
  {dataType: 'ENUM', title: 'Dropdown', description: 'Pick one option', icon: 'arrow_drop_down_circle'},
  {dataType: 'MULTI_ENUM', multiEnumUi: 'select', title: 'Multi-select', description: 'Pick multiple from a dropdown', icon: 'library_add_check'},
  {dataType: 'MULTI_ENUM', multiEnumUi: 'checkbox', title: 'Multi checkbox', description: 'Pick multiple with checkboxes', icon: 'checklist'},
  {dataType: 'DATE', title: 'Date picker', description: 'Calendar date', icon: 'event'},
  {dataType: 'RANGE', title: 'Range slider', description: 'Min–max slider', icon: 'tune'},
];

export function normalizeCategoryPath(path: string | null | undefined): string {
  if (!path?.trim() || path.trim() === '*') {
    return '*';
  }
  return path
    .split('/')
    .map(segment => segment.trim().toLowerCase())
    .filter(Boolean)
    .join('/');
}

export function parseValidationObject(raw?: string | null): Record<string, unknown> {
  if (!raw?.trim()) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? {...(parsed as Record<string, unknown>)}
      : {};
  } catch {
    return {};
  }
}

export function getFormSection(field: Pick<AttributeDefinitionView, 'validationJson'>): string | null {
  const section = parseValidationObject(field.validationJson).formSection;
  return typeof section === 'string' && section.trim() ? section.trim() : null;
}

/** Buyer-facing detail/browse: false only when validationJson.buyerVisible === false. */
export function isBuyerVisibleAttribute(
  field: Pick<AttributeDefinitionView, 'validationJson'> | Pick<ListingAttributeValueView, 'buyerVisible' | 'code'> | {validationJson?: string; buyerVisible?: boolean},
): boolean {
  if (field && typeof (field as ListingAttributeValueView).buyerVisible === 'boolean') {
    return (field as ListingAttributeValueView).buyerVisible !== false;
  }
  const validation = parseValidationObject((field as AttributeDefinitionView)?.validationJson);
  if (validation.buyerVisible === false || validation.buyerVisible === 'false') {
    return false;
  }
  return true;
}

export function getShowWhen(field: Pick<AttributeDefinitionView, 'validationJson'>): AttributeConditionRule | null {
  return parseConditionRule(parseValidationObject(field.validationJson).showWhen);
}

export function getRequiredWhen(field: Pick<AttributeDefinitionView, 'validationJson'>): AttributeConditionRule | null {
  return parseConditionRule(parseValidationObject(field.validationJson).requiredWhen);
}

function parseConditionRule(raw: unknown): AttributeConditionRule | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const attribute = (raw as Record<string, unknown>).attribute;
  if (typeof attribute !== 'string' || !attribute.trim()) {
    return null;
  }
  return {
    attribute: attribute.trim(),
    in: showWhenValueList((raw as Record<string, unknown>).in),
    notIn: showWhenValueList((raw as Record<string, unknown>).notIn),
  };
}

export function controlValueTokens(value: unknown): string[] {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map(entry => normalizeShowWhenToken(`${entry}`))
      .filter(Boolean);
  }
  if (typeof value === 'boolean') {
    return [value ? 'true' : 'false'];
  }
  const token = normalizeShowWhenToken(`${value}`);
  return token ? [token] : [];
}

function normalizeShowWhenToken(value: string): string {
  return `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function evaluateAttributeCondition(
  rule: AttributeConditionRule | null | undefined,
  allValues: Record<string, unknown>
): boolean {
  if (!rule?.attribute?.trim()) {
    return true;
  }

  const tokens = controlValueTokens(allValues?.[rule.attribute]);
  if (!tokens.length) {
    return false;
  }

  if (rule.in?.length) {
    const allowed = new Set(rule.in.map(entry => normalizeShowWhenToken(entry)));
    if (!tokens.some(token => allowed.has(token))) {
      return false;
    }
  }

  if (rule.notIn?.length) {
    const blocked = new Set(rule.notIn.map(entry => normalizeShowWhenToken(entry)));
    if (tokens.some(token => blocked.has(token))) {
      return false;
    }
  }

  return true;
}

export function isFieldVisible(
  field: Pick<AttributeDefinitionView, 'validationJson'>,
  allValues: Record<string, unknown>
): boolean {
  const rule = getShowWhen(field);
  if (!rule) {
    return true;
  }
  return evaluateAttributeCondition(rule, allValues);
}

/**
 * Field is required when visible and either statically required or requiredWhen matches.
 * Hidden fields are never required.
 */
export function isFieldRequired(
  field: Pick<AttributeDefinitionView, 'required' | 'validationJson'>,
  allValues: Record<string, unknown>
): boolean {
  if (!isFieldVisible(field, allValues)) {
    return false;
  }
  if (field.required) {
    return true;
  }
  const rule = getRequiredWhen(field);
  return rule ? evaluateAttributeCondition(rule, allValues) : false;
}

export function parseShowWhenList(raw: string | null | undefined): string[] {
  return `${raw || ''}`
    .split(/[,;\n]/)
    .map(entry => normalizeShowWhenToken(entry))
    .filter(Boolean);
}

export function formatShowWhenList(values: string[] | null | undefined): string {
  return (values || [])
    .map(entry => entry.trim())
    .filter(Boolean)
    .join(', ');
}

export function showWhenDraftFromValidation(validation: Record<string, unknown>): Pick<
  FormFieldDraft,
  'showWhenEnabled' | 'showWhenAttribute' | 'showWhenIn' | 'showWhenNotIn'
> {
  const draft = conditionDraftFromValidation(validation.showWhen);
  return {
    showWhenEnabled: draft.enabled,
    showWhenAttribute: draft.attribute,
    showWhenIn: draft.inList,
    showWhenNotIn: draft.notInList,
  };
}

export function requiredWhenDraftFromValidation(validation: Record<string, unknown>): Pick<
  FormFieldDraft,
  'requiredWhenEnabled' | 'requiredWhenAttribute' | 'requiredWhenIn' | 'requiredWhenNotIn'
> {
  const draft = conditionDraftFromValidation(validation.requiredWhen);
  return {
    requiredWhenEnabled: draft.enabled,
    requiredWhenAttribute: draft.attribute,
    requiredWhenIn: draft.inList,
    requiredWhenNotIn: draft.notInList,
  };
}

function conditionDraftFromValidation(raw: unknown): {
  enabled: boolean;
  attribute: string;
  inList: string;
  notInList: string;
} {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {enabled: false, attribute: '', inList: '', notInList: ''};
  }
  const rule = raw as Record<string, unknown>;
  const attribute = typeof rule.attribute === 'string' ? rule.attribute.trim() : '';
  const inList = Array.isArray(rule.in)
    ? formatShowWhenList(rule.in.map(entry => `${entry}`))
    : '';
  const notInList = Array.isArray(rule.notIn)
    ? formatShowWhenList(rule.notIn.map(entry => `${entry}`))
    : '';
  return {
    enabled: !!attribute && (!!inList || !!notInList),
    attribute,
    inList,
    notInList,
  };
}

export function buildShowWhenValidation(field: Pick<
  FormFieldDraft,
  'showWhenEnabled' | 'showWhenAttribute' | 'showWhenIn' | 'showWhenNotIn'
>): AttributeConditionRule | null {
  return buildConditionValidation({
    enabled: !!field.showWhenEnabled,
    attribute: field.showWhenAttribute,
    inList: field.showWhenIn,
    notInList: field.showWhenNotIn,
  });
}

export function buildRequiredWhenValidation(field: Pick<
  FormFieldDraft,
  'requiredWhenEnabled' | 'requiredWhenAttribute' | 'requiredWhenIn' | 'requiredWhenNotIn'
>): AttributeConditionRule | null {
  return buildConditionValidation({
    enabled: !!field.requiredWhenEnabled,
    attribute: field.requiredWhenAttribute,
    inList: field.requiredWhenIn,
    notInList: field.requiredWhenNotIn,
  });
}

function buildConditionValidation(input: {
  enabled: boolean;
  attribute?: string;
  inList?: string;
  notInList?: string;
}): AttributeConditionRule | null {
  if (!input.enabled) {
    return null;
  }
  const attribute = `${input.attribute || ''}`.trim();
  if (!attribute) {
    return null;
  }
  const rule: AttributeConditionRule = {attribute};
  const inValues = parseShowWhenList(input.inList);
  const notInValues = parseShowWhenList(input.notInList);
  if (inValues.length) {
    rule.in = inValues;
  }
  if (notInValues.length) {
    rule.notIn = notInValues;
  }
  return rule.in?.length || rule.notIn?.length ? rule : null;
}

export function listControllingFieldCodes(
  sections: FormBuilderSection[],
  currentField?: Pick<FormFieldDraft, 'draftId' | 'code'>
): string[] {
  const codes: string[] = [];
  const currentCode = `${currentField?.code || ''}`.trim().toLowerCase();
  for (const section of sections || []) {
    for (const field of section.fields || []) {
      if (currentField?.draftId && field.draftId === currentField.draftId) {
        continue;
      }
      const code = `${field.code || ''}`.trim().toLowerCase();
      if (!code || code === currentCode || codes.includes(code)) {
        continue;
      }
      codes.push(code);
    }
  }
  return codes.sort();
}

export function controllingFieldOptions(
  sections: FormBuilderSection[],
  attributeCode: string
): Array<{key: string; label: string}> {
  const code = `${attributeCode || ''}`.trim().toLowerCase();
  if (!code) {
    return [];
  }
  for (const section of sections || []) {
    for (const field of section.fields || []) {
      if (`${field.code || ''}`.trim().toLowerCase() !== code || !isEnumDataType(field.dataType)) {
        continue;
      }
      return (field.options || [])
        .filter(option => option.key?.trim() && option.label?.trim())
        .map(option => ({
          key: option.key.trim().toLowerCase(),
          label: option.label.trim(),
        }));
    }
  }
  return [];
}

export function createEmptyBuilderSections(): FormBuilderSection[] {
  return [{
    id: `section-${Date.now()}`,
    title: 'Listing details',
    fields: [],
  }];
}

export function createDraftFromTemplate(template: DataTypeTemplate): FormFieldDraft {
  const isEnum = template.dataType === 'ENUM' || template.dataType === 'MULTI_ENUM';
  const multiEnumUi = template.dataType === 'MULTI_ENUM'
    ? normalizeMultiEnumUi(template.multiEnumUi)
    : undefined;
  return {
    draftId: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    dataType: template.dataType,
    code: '',
    label: '',
    unit: '',
    required: false,
    filterable: false,
    searchable: false,
    options: isEnum ? [{key: '', label: '', sortOrder: 0}] : [],
    rangeMin: 0,
    rangeMax: 100,
    rangeStep: 1,
    textMinLength: null,
    textMaxLength: null,
    textPlaceholder: '',
    numberMin: null,
    numberMax: null,
    collapsed: true,
    multiEnumUi,
    optionsCollapsed: true,
    optionsJsonMode: false,
    optionsJsonDraft: isEnum ? exportFieldOptionsJson([{key: '', label: '', sortOrder: 0}]) : '',
    showWhenEnabled: false,
    showWhenAttribute: '',
    showWhenIn: '',
    showWhenNotIn: '',
    requiredWhenEnabled: false,
    requiredWhenAttribute: '',
    requiredWhenIn: '',
    requiredWhenNotIn: '',
  };
}

export function isEnumDataType(dataType: string): boolean {
  return dataType === 'ENUM' || dataType === 'MULTI_ENUM';
}

export function isMultiEnumDataType(dataType: string): boolean {
  return dataType === 'MULTI_ENUM';
}

export function normalizeMultiEnumUi(raw: unknown): MultiEnumUi {
  return raw === 'checkbox' ? 'checkbox' : 'select';
}

export function getMultiEnumUi(
  field: Pick<FormFieldDraft, 'dataType' | 'multiEnumUi'> & {validationJson?: string | null}
): MultiEnumUi {
  if (field.dataType !== 'MULTI_ENUM') {
    return 'select';
  }
  if (field.multiEnumUi) {
    return normalizeMultiEnumUi(field.multiEnumUi);
  }
  if (field.validationJson) {
    return normalizeMultiEnumUi(parseValidationObject(field.validationJson).multiEnumUi);
  }
  return 'select';
}

export function isMultiEnumCheckboxUi(
  field: Pick<FormFieldDraft, 'dataType' | 'multiEnumUi'> & {validationJson?: string | null}
): boolean {
  return field.dataType === 'MULTI_ENUM' && getMultiEnumUi(field) === 'checkbox';
}

export function isRangeDataType(dataType: string): boolean {
  return dataType === 'RANGE';
}

export function isStringDataType(dataType: string): boolean {
  return dataType === 'STRING';
}

export function isNumberDataType(dataType: string): boolean {
  return dataType === 'INTEGER' || dataType === 'DECIMAL';
}

export function isDataTypeTemplate(data: unknown): data is DataTypeTemplate {
  return !!data
    && typeof data === 'object'
    && 'dataType' in data
    && 'title' in data
    && typeof (data as DataTypeTemplate).dataType === 'string';
}

export function isFormFieldDraft(data: unknown): data is FormFieldDraft {
  return !!data
    && typeof data === 'object'
    && 'draftId' in data
    && 'dataType' in data;
}

export function dataTypeLabel(dataType: string, multiEnumUi?: MultiEnumUi): string {
  const template = DATA_TYPE_TEMPLATES.find(row => {
    if (row.dataType !== dataType) {
      return false;
    }
    if (dataType === 'MULTI_ENUM') {
      return normalizeMultiEnumUi(row.multiEnumUi) === normalizeMultiEnumUi(multiEnumUi);
    }
    return !row.multiEnumUi;
  });
  return template?.title || dataType;
}

export function validateFormBuilder(
  categoryPath: string,
  sections: FormBuilderSection[]
): FormBuilderValidationIssue[] {
  const issues: FormBuilderValidationIssue[] = [];
  if (!normalizeCategoryPath(categoryPath)) {
    issues.push({sectionIndex: -1, fieldIndex: -1, message: 'Category path is required.'});
  }

  const fieldCount = sections.reduce((count, section) => count + section.fields.length, 0);
  if (!fieldCount) {
    issues.push({sectionIndex: -1, fieldIndex: -1, message: 'Add at least one field to a section.'});
    return issues;
  }

  const seenCodes = new Set<string>();
  const allCodes = new Set<string>();
  for (const section of sections) {
    for (const field of section.fields) {
      const code = `${field.code || ''}`.trim().toLowerCase();
      if (code) {
        allCodes.add(code);
      }
    }
  }

  sections.forEach((section, sectionIndex) => {
    if (!section.title.trim()) {
      issues.push({
        sectionIndex,
        fieldIndex: -1,
        message: `Section ${sectionIndex + 1} needs a title.`,
      });
    }

    section.fields.forEach((field, fieldIndex) => {
      const code = field.code.trim().toLowerCase();
      const label = field.label.trim();

      if (!code || !label) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: 'Code and label are required.',
        });
      } else if (!/^[a-z][a-z0-9_]*$/.test(code)) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: 'Code must start with a letter and use lowercase letters, numbers, or underscores.',
        });
      } else if (seenCodes.has(code)) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: `Duplicate code "${code}" in this form.`,
        });
      } else {
        seenCodes.add(code);
      }

      if (isEnumDataType(field.dataType)) {
        const options = (field.options || []).filter(option => option.key?.trim() && option.label?.trim());
        if (!options.length) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'Add at least one dropdown option.',
          });
        }
      }

      if (isRangeDataType(field.dataType) && field.rangeMax <= field.rangeMin) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: 'Slider max must be greater than min.',
        });
      }

      if (isStringDataType(field.dataType)
        && field.textMinLength != null
        && field.textMaxLength != null
        && field.textMaxLength < field.textMinLength) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: 'Text max length must be greater than or equal to min length.',
        });
      }

      if (isNumberDataType(field.dataType)
        && field.numberMin != null
        && field.numberMax != null
        && field.numberMax < field.numberMin) {
        issues.push({
          sectionIndex,
          fieldIndex,
          message: 'Number max must be greater than or equal to min.',
        });
      }

      if (field.showWhenEnabled) {
        const attribute = `${field.showWhenAttribute || ''}`.trim().toLowerCase();
        const inValues = parseShowWhenList(field.showWhenIn);
        const notInValues = parseShowWhenList(field.showWhenNotIn);
        if (!attribute) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'Choose a controlling field for conditional visibility.',
          });
        } else if (!allCodes.has(attribute)) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: `Controlling field "${attribute}" must exist in this form.`,
          });
        } else if (attribute === code) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'A field cannot control its own visibility.',
          });
        } else if (!inValues.length && !notInValues.length) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'Add at least one value for "show when in" or "hide when in".',
          });
        }
      }

      if (field.requiredWhenEnabled) {
        const attribute = `${field.requiredWhenAttribute || ''}`.trim().toLowerCase();
        const inValues = parseShowWhenList(field.requiredWhenIn);
        const notInValues = parseShowWhenList(field.requiredWhenNotIn);
        if (!attribute) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'Choose a controlling field for conditional required.',
          });
        } else if (!allCodes.has(attribute)) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: `Required-when controlling field "${attribute}" must exist in this form.`,
          });
        } else if (attribute === code) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'A field cannot make itself conditionally required.',
          });
        } else if (!inValues.length && !notInValues.length) {
          issues.push({
            sectionIndex,
            fieldIndex,
            message: 'Add at least one value for "required when in" or "not required when in".',
          });
        }
      }
    });
  });

  return issues;
}

export function builderSectionsToBulkPayload(
  categoryPath: string,
  sections: FormBuilderSection[],
  mode: 'upsert' | 'create_only' = 'upsert'
): AttributeBulkImportPayload {
  const fields: AttributeBulkFieldPayload[] = [];

  sections.forEach((section, sectionIndex) => {
    section.fields.forEach((field, fieldIndex) => {
      const payload: AttributeBulkFieldPayload = {
        code: field.code.trim().toLowerCase(),
        label: field.label.trim(),
        dataType: field.dataType,
        unit: field.unit?.trim() || undefined,
        required: field.required,
        filterable: field.filterable,
        searchable: field.searchable,
        displayOrder: sectionIndex * 1000 + fieldIndex * 10,
        active: true,
      };

      const validation: Record<string, unknown> = {formSection: section.title.trim()};
      if (isRangeDataType(field.dataType)) {
        validation.min = Number(field.rangeMin);
        validation.max = Number(field.rangeMax);
        validation.step = Number(field.rangeStep) || 1;
      } else if (isStringDataType(field.dataType)) {
        if (field.textMinLength != null) {
          validation.minLength = Number(field.textMinLength);
        }
        if (field.textMaxLength != null) {
          validation.maxLength = Number(field.textMaxLength);
        }
        if (field.textPlaceholder?.trim()) {
          validation.placeholder = field.textPlaceholder.trim();
        }
      } else if (isNumberDataType(field.dataType)) {
        if (field.numberMin != null) {
          validation.min = Number(field.numberMin);
        }
        if (field.numberMax != null) {
          validation.max = Number(field.numberMax);
        }
      } else if (isMultiEnumDataType(field.dataType) && getMultiEnumUi(field) === 'checkbox') {
        validation.multiEnumUi = 'checkbox';
      }
      const showWhen = buildShowWhenValidation(field);
      if (showWhen) {
        validation.showWhen = showWhen;
      }
      const requiredWhen = buildRequiredWhenValidation(field);
      if (requiredWhen) {
        validation.requiredWhen = requiredWhen;
      }
      payload.validation = validation;

      if (isEnumDataType(field.dataType)) {
        payload.options = (field.options || [])
          .filter(option => option.key?.trim() && option.label?.trim())
          .map((option, index) => ({
            key: option.key.trim().toLowerCase(),
            label: option.label.trim(),
            sortOrder: option.sortOrder ?? index,
          }));
      }

      fields.push(payload);
    });
  });

  return {
    categoryPath: normalizeCategoryPath(categoryPath),
    mode,
    replaceLayoutFields: mode !== 'create_only',
    fields,
  };
}

export function exportFormBuilderDraft(
  categoryPath: string,
  sections: FormBuilderSection[],
  importMode: 'upsert' | 'create_only' = 'upsert'
): string {
  const payload: FormBuilderDraftPayload = {
    version: 1,
    categoryPath: normalizeCategoryPath(categoryPath),
    importMode,
    sections: (sections || []).map(section => ({
      title: section.title,
      fields: (section.fields || []).map(field => fieldDraftToExport(field)),
    })),
  };
  return JSON.stringify(payload, null, 2);
}

export function parseFormBuilderDraft(raw: string): {draft?: FormBuilderDraftPayload; error?: string} {
  const cleaned = sanitizeJsonPaste(raw);
  if (!cleaned) {
    return {error: 'Paste a form builder JSON draft.'};
  }
  try {
    const parsed = JSON.parse(cleaned) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') {
      return {error: 'JSON must be an object.'};
    }
    if (Array.isArray(parsed.sections)) {
      return parseBuilderSectionsDraft(parsed as Partial<FormBuilderDraftPayload>);
    }
    if (Array.isArray(parsed.fields)) {
      return {draft: bulkPayloadToBuilderDraft(parsed)};
    }
    return {error: 'JSON must include a sections array (builder draft) or fields array (bulk import).'};
  } catch {
    return {error: 'Invalid JSON.'};
  }
}

function parseBuilderSectionsDraft(parsed: Partial<FormBuilderDraftPayload>): {draft?: FormBuilderDraftPayload; error?: string} {
  if (parsed.version != null && parsed.version !== 1) {
    return {error: 'Unsupported draft version. Expected version 1.'};
  }
  return {
    draft: {
      version: 1,
      categoryPath: normalizeCategoryPath(parsed.categoryPath || '*'),
      importMode: parsed.importMode === 'create_only' ? 'create_only' : 'upsert',
      sections: (parsed.sections || []).map(section => ({
        title: `${section?.title || ''}`.trim() || 'Section',
        fields: Array.isArray(section?.fields) ? section.fields.map(field => normalizeFieldExport(field)) : [],
      })),
    },
  };
}

function bulkPayloadToBuilderDraft(parsed: Record<string, unknown>): FormBuilderDraftPayload {
  const fields = (parsed.fields as AttributeBulkFieldPayload[]) || [];
  const sectionOrder: string[] = [];
  const sectionMap = new Map<string, FormFieldDraftExport[]>();
  const sorted = [...fields].sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0));

  for (const field of sorted) {
    const validation = field.validation || parseValidationObject(field.validationJson);
    const title = `${validation.formSection || DEFAULT_FORM_SECTION_TITLE}`.trim() || DEFAULT_FORM_SECTION_TITLE;
    if (!sectionMap.has(title)) {
      sectionMap.set(title, []);
      sectionOrder.push(title);
    }
    sectionMap.get(title)!.push(bulkFieldToExport(field, validation));
  }

  const mode = parsed.mode ?? parsed.importMode;
  return {
    version: 1,
    categoryPath: normalizeCategoryPath(`${parsed.categoryPath || '*'}`),
    importMode: mode === 'create_only' ? 'create_only' : 'upsert',
    sections: sectionOrder.map(title => ({
      title,
      fields: sectionMap.get(title) || [],
    })),
  };
}

function bulkFieldToExport(
  field: AttributeBulkFieldPayload,
  validation: Record<string, unknown>
): FormFieldDraftExport {
  const dataType = `${field.dataType || 'STRING'}`.trim().toUpperCase();
  const exportField = normalizeFieldExport({
    dataType,
    code: field.code,
    label: field.label,
    unit: field.unit,
    required: field.required,
    filterable: field.filterable,
    searchable: field.searchable,
    options: (field.options || []).map((option, index) => ({
      key: option.key,
      label: option.label,
      sortOrder: option.sortOrder ?? index,
    })),
    rangeMin: numberOr(validation.min, 0),
    rangeMax: numberOr(validation.max, 100),
    rangeStep: numberOr(validation.step, 1),
    textMinLength: nullableNumber(validation.minLength),
    textMaxLength: nullableNumber(validation.maxLength),
    textPlaceholder: validation.placeholder != null ? `${validation.placeholder}` : '',
    numberMin: isNumberDataType(dataType) ? nullableNumber(validation.min) : null,
    numberMax: isNumberDataType(dataType) ? nullableNumber(validation.max) : null,
    collapsed: false,
    multiEnumUi: isMultiEnumDataType(dataType) ? normalizeMultiEnumUi(validation.multiEnumUi) : undefined,
    ...showWhenDraftFromValidation(validation),
    ...requiredWhenDraftFromValidation(validation),
  });
  return exportField;
}

export function importFormBuilderDraft(draft: FormBuilderDraftPayload): FormBuilderDraftImportResult {
  const baseId = Date.now();
  const sections = (draft.sections || []).map((section, sectionIndex) => ({
    id: `section-${baseId}-${sectionIndex}-${Math.random().toString(36).slice(2, 6)}`,
    title: section.title?.trim() || `Section ${sectionIndex + 1}`,
    fields: (section.fields || []).map((field, fieldIndex) => importFieldDraft(field, sectionIndex * 1000 + fieldIndex)),
  }));

  return {
    categoryPath: normalizeCategoryPath(draft.categoryPath),
    importMode: draft.importMode === 'create_only' ? 'create_only' : 'upsert',
    sections: sections.length ? sections : createEmptyBuilderSections(),
  };
}

export function attributeDefinitionsToBuilderDraft(
  categoryPath: string,
  attributes: AttributeDefinitionView[],
  importMode: 'upsert' | 'create_only' = 'upsert'
): FormBuilderDraftPayload | null {
  const path = normalizeCategoryPath(categoryPath);
  const atPath = (attributes || [])
    .filter(row => normalizeCategoryPath(row.categoryPath) === path)
    .sort((left, right) => {
      const orderDiff = (left.displayOrder ?? 0) - (right.displayOrder ?? 0);
      return orderDiff !== 0 ? orderDiff : left.code.localeCompare(right.code);
    });

  if (!atPath.length) {
    return null;
  }

  const sectioned = atPath.filter(row => !!getFormSection(row));
  const rows = sectioned.length ? sectioned : atPath;

  const draft = bulkPayloadToBuilderDraft({
    categoryPath: path,
    mode: importMode,
    fields: rows.map(attributeDefinitionToBulkField),
  });
  draft.importMode = importMode;
  return draft;
}

function attributeDefinitionToBulkField(row: AttributeDefinitionView): AttributeBulkFieldPayload {
  const validation = parseValidationObject(row.validationJson);
  return {
    code: row.code,
    label: row.label,
    dataType: row.dataType,
    unit: row.unit,
    required: row.required,
    filterable: row.filterable,
    searchable: row.searchable,
    displayOrder: row.displayOrder,
    validation,
    options: (row.options || []).map((option, index) => ({
      key: option.key,
      label: option.label,
      sortOrder: option.sortOrder ?? index,
    })),
  };
}

function fieldDraftToExport(field: FormFieldDraft): FormFieldDraftExport {
  return {
    dataType: field.dataType,
    code: field.code,
    label: field.label,
    unit: field.unit,
    required: field.required,
    filterable: field.filterable,
    searchable: field.searchable,
    options: (field.options || []).map((option, index) => ({
      key: option.key,
      label: option.label,
      sortOrder: option.sortOrder ?? index,
    })),
    rangeMin: field.rangeMin,
    rangeMax: field.rangeMax,
    rangeStep: field.rangeStep,
    textMinLength: field.textMinLength,
    textMaxLength: field.textMaxLength,
    textPlaceholder: field.textPlaceholder,
    numberMin: field.numberMin,
    numberMax: field.numberMax,
    collapsed: field.collapsed,
    multiEnumUi: field.multiEnumUi,
    showWhenEnabled: !!field.showWhenEnabled,
    showWhenAttribute: field.showWhenAttribute || '',
    showWhenIn: field.showWhenIn || '',
    showWhenNotIn: field.showWhenNotIn || '',
    requiredWhenEnabled: !!field.requiredWhenEnabled,
    requiredWhenAttribute: field.requiredWhenAttribute || '',
    requiredWhenIn: field.requiredWhenIn || '',
    requiredWhenNotIn: field.requiredWhenNotIn || '',
  };
}

function normalizeFieldExport(raw: Partial<FormFieldDraftExport>): FormFieldDraftExport {
  const dataType = `${raw?.dataType || 'STRING'}`.trim().toUpperCase();
  const allowed = new Set(DATA_TYPE_TEMPLATES.map(template => template.dataType));
  const resolvedType = allowed.has(dataType) ? dataType : 'STRING';
  const isEnum = isEnumDataType(resolvedType);
  return {
    dataType: resolvedType,
    code: `${raw?.code || ''}`,
    label: `${raw?.label || ''}`,
    unit: `${raw?.unit || ''}`,
    required: !!raw?.required,
    filterable: raw?.filterable !== false,
    searchable: !!raw?.searchable,
    options: isEnum
      ? (raw?.options || []).map((option, index) => ({
        key: `${option?.key || ''}`,
        label: `${option?.label || ''}`,
        sortOrder: option?.sortOrder ?? index,
      }))
      : [],
    rangeMin: numberOr(raw?.rangeMin, 0),
    rangeMax: numberOr(raw?.rangeMax, 100),
    rangeStep: numberOr(raw?.rangeStep, 1),
    textMinLength: nullableNumber(raw?.textMinLength),
    textMaxLength: nullableNumber(raw?.textMaxLength),
    textPlaceholder: `${raw?.textPlaceholder || ''}`,
    numberMin: nullableNumber(raw?.numberMin),
    numberMax: nullableNumber(raw?.numberMax),
    collapsed: !!raw?.collapsed,
    multiEnumUi: isMultiEnumDataType(resolvedType) ? normalizeMultiEnumUi(raw?.multiEnumUi) : undefined,
    showWhenEnabled: !!raw?.showWhenEnabled,
    showWhenAttribute: `${raw?.showWhenAttribute || ''}`,
    showWhenIn: `${raw?.showWhenIn || ''}`,
    showWhenNotIn: `${raw?.showWhenNotIn || ''}`,
    requiredWhenEnabled: !!raw?.requiredWhenEnabled,
    requiredWhenAttribute: `${raw?.requiredWhenAttribute || ''}`,
    requiredWhenIn: `${raw?.requiredWhenIn || ''}`,
    requiredWhenNotIn: `${raw?.requiredWhenNotIn || ''}`,
  };
}

function importFieldDraft(field: FormFieldDraftExport, index: number): FormFieldDraft {
  const options = (field.options || []).map((option, optionIndex) => ({
    key: option.key,
    label: option.label,
    sortOrder: option.sortOrder ?? optionIndex,
  }));
  return {
    draftId: `draft-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    ...field,
    options,
    multiEnumUi: isMultiEnumDataType(field.dataType) ? normalizeMultiEnumUi(field.multiEnumUi) : undefined,
    collapsed: field.collapsed ?? true,
    optionsCollapsed: true,
    optionsJsonMode: false,
    optionsJsonDraft: isEnumDataType(field.dataType) ? exportFieldOptionsJson(options) : '',
    showWhenEnabled: !!field.showWhenEnabled,
    showWhenAttribute: field.showWhenAttribute || '',
    showWhenIn: field.showWhenIn || '',
    showWhenNotIn: field.showWhenNotIn || '',
    requiredWhenEnabled: !!field.requiredWhenEnabled,
    requiredWhenAttribute: field.requiredWhenAttribute || '',
    requiredWhenIn: field.requiredWhenIn || '',
    requiredWhenNotIn: field.requiredWhenNotIn || '',
  };
}

export function sanitizeJsonPaste(raw: string): string {
  let text = `${raw || ''}`.trim();
  if (!text.startsWith('```')) {
    return text;
  }
  return text
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();
}

export function exportFieldOptionsJson(
  options: Array<{key: string; label: string; sortOrder: number}>
): string {
  const cleaned = (options || []).map((option, index) => ({
    key: option.key ?? '',
    label: option.label ?? '',
    sortOrder: option.sortOrder ?? index,
  }));
  return JSON.stringify(cleaned, null, 2);
}

export function parseFieldOptionsJson(
  raw: string
): {options?: Array<{key: string; label: string; sortOrder: number}>; error?: string} {
  const cleaned = sanitizeJsonPaste(raw);
  if (!cleaned) {
    return {error: 'Paste a JSON array of options.'};
  }
  try {
    const parsed = JSON.parse(cleaned) as unknown;
    if (!Array.isArray(parsed)) {
      return {error: 'Options JSON must be an array.'};
    }
    const options = parsed.map((row, index) => {
      const record = (row && typeof row === 'object') ? row as Record<string, unknown> : {};
      return {
        key: `${record.key ?? ''}`.trim().toLowerCase(),
        label: `${record.label ?? ''}`.trim(),
        sortOrder: Number.isFinite(Number(record.sortOrder)) ? Number(record.sortOrder) : index,
      };
    });
    if (!options.length) {
      return {error: 'Add at least one option.'};
    }
    if (options.some(option => !option.key || !option.label)) {
      return {error: 'Each option needs a key and label.'};
    }
    return {options};
  } catch {
    return {error: 'Invalid options JSON.'};
  }
}

function numberOr(value: unknown, fallback: number): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function showWhenValueList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const entries = value
    .map(entry => normalizeShowWhenToken(`${entry}`))
    .filter(Boolean);
  return entries.length ? entries : undefined;
}

export function filterableAttributeFields(schema: AttributeDefinitionView[]): AttributeDefinitionView[] {
  return visibleFormFields(schema).filter(field => field.filterable);
}

export function visibleFormFields(schema: AttributeDefinitionView[]): AttributeDefinitionView[] {
  const fields: AttributeDefinitionView[] = [];
  for (const section of groupSchemaBySections(schema)) {
    fields.push(...section.fields);
  }
  return fields;
}

export function groupFilterableSchemaBySections(schema: AttributeDefinitionView[]): AttributeSectionGroup[] {
  return groupSchemaBySections(schema)
    .map(section => ({
      title: section.title,
      fields: section.fields.filter(field => field.filterable),
    }))
    .filter(section => section.fields.length > 0);
}

export function visibleFilterFields(
  schema: AttributeDefinitionView[],
  values: Record<string, unknown> = {}
): AttributeDefinitionView[] {
  return visibleFormFields(schema).filter(field => field.filterable && isFieldVisible(field, values));
}

export function groupSchemaBySections(schema: AttributeDefinitionView[]): AttributeSectionGroup[] {
  const effective = [...(schema || [])].sort((left, right) => {
    const orderDiff = (left.displayOrder ?? 0) - (right.displayOrder ?? 0);
    return orderDiff !== 0 ? orderDiff : left.code.localeCompare(right.code);
  });

  const hasConfiguredLayout = effective.some(field => !!getFormSection(field));
  if (!hasConfiguredLayout) {
    return effective.length ? [{title: '', fields: effective}] : [];
  }

  const grouped = new Map<string, AttributeDefinitionView[]>();

  for (const field of effective) {
    const title = getFormSection(field);
    if (!title) {
      continue;
    }
    const bucket = grouped.get(title) || [];
    bucket.push(field);
    grouped.set(title, bucket);
  }

  return Array.from(grouped.entries())
    .map(([title, fields]) => ({title, fields}))
    .sort((left, right) => {
      const leftOrder = left.fields[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.fields[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.title.localeCompare(right.title);
    });
}
